import React, { useRef, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import Decimal from "decimal.js";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import getFoliateCSS from "./foliateCSS";
import useReaderSettings from "../../hooks/useReaderSettings";
import useSwipeGestures from "../../hooks/useSwipeGestures";
import useKeyboardNavigation from "../../hooks/useKeyboardNavigation";

/**
 * Компонент для рендеринга книг с помощью foliate-js
 * Поддерживает FB2, EPUB и другие форматы
 */
// Note: We no longer use foliate's TOC for navigation, we use custom TOC with characterPosition
// Keeping this commented for reference
// const flattenToc = (tocItems) => {
//   let flat = [];
//   tocItems.forEach((item) => {
//     flat.push(item);
//     if (item.subitems && Array.isArray(item.subitems)) {
//       flat = flat.concat(flattenToc(item.subitems));
//     }
//     if (item.children && Array.isArray(item.children)) {
//       flat = flat.concat(flattenToc(item.children));
//     }
//   });
//   return flat;
// };

export default function FoliateReader({
  bookUrl,
  bookMetadata,
  characterLength,
  initialFraction,
  initialCfi,
  onPositionChange,
  onProgressChange,
  onError,
  onUIToggle,
  children,
}) {
  const containerRef = useRef(null);
  const viewRef = useRef(null);
  const overlayRef = useRef(null);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCharacters, setTotalCharacters] = useState(0);
  const [currentCharacter, setCurrentCharacter] = useState(0);
  const [currentChapter, setCurrentChapter] = useState(null);
  // Note: We no longer use foliate's TOC for navigation, we use custom TOC with characterPosition
  // const [bookToc, setBookToc] = useState(null);
  const [currentCfi, setCurrentCfi] = useState(null);

  // Хук для управления настройками читалки
  const {
    fontSize,
    readerTheme,
    spacing,
    justify,
    hyphenate,
    updateFontSize,
    updateTheme,
    updateSpacing,
    updateJustify,
    updateHyphenate,
  } = useReaderSettings();

  const [readingProgress, setReadingProgress] = useState(0);
  // Используем только постраничный режим
  const readingMode = "paginated";

  // Hook for URL search params management
  const [searchParams, setSearchParams] = useSearchParams();

  // Используем useRef для стабильных ссылок на callback'и
  const callbacksRef = useRef({
    onPositionChange,
    onProgressChange,
    onError,
    onUIToggle,
  });

  // Обновляем ссылки на callback'и при каждом рендере
  useEffect(() => {
    callbacksRef.current = {
      onPositionChange,
      onProgressChange,
      onError,
      onUIToggle,
    };
  });

  // Мемоизированные callback'и для жестов
  const handleGoLeft = useCallback(() => viewRef.current?.goLeft?.(), []);
  const handleGoRight = useCallback(() => viewRef.current?.goRight?.(), []);
  const handleUIToggle = useCallback(
    () => callbacksRef.current.onUIToggle?.(),
    []
  );

  // Обработка жестов свайпов и тапов для навигации
  useSwipeGestures({
    elementRef: overlayRef,
    onGoLeft: handleGoLeft,
    onGoRight: handleGoRight,
    onUIToggle: handleUIToggle,
  });

  // Инициализация foliate-js (без initialPosition в зависимостях)
  const initializeFoliate = useCallback(async () => {
    if (!bookUrl || !containerRef.current) return;

    // Флаг инициализации для предотвращения вызова onProgressChange во время загрузки
    let isInitializing = true;

    // Create new AbortController for this initialization
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      if (!isMountedRef.current) return;
      setIsLoading(true);
      setError(null);

      await import("foliate-js/view.js");
      const { makeFB2 } = await import("foliate-js/fb2.js");

      if (!isMountedRef.current) return;

      // Создаем foliate-view элемент согласно официальной документации
      const view = document.createElement("foliate-view");
      viewRef.current = view;

      // Добавляем view в контейнер
      containerRef.current.appendChild(view);
      // Обработчик события relocate для отслеживания позиции
      view.addEventListener("relocate", (event) => {
        if (!isMountedRef.current) return;

        const { fraction, location, cfi } = event.detail;

        // Получаем общее количество символов из метаданных
        const characterLength = bookMetadata?.characterLength || 100000; // fallback значение
        setTotalCharacters(characterLength);

        const characterPosition = Math.round(fraction * characterLength);
        const progressPercent = fraction * 100;

        setCurrentCharacter(characterPosition);
        setReadingProgress(progressPercent);
        setCurrentCfi(cfi);

        // Определяем текущую главу на основе позиции
        if (bookMetadata?.tableOfContents) {
          const currentChapterItem = bookMetadata.tableOfContents
            .slice()
            .reverse()
            .find(
              (chapter) => characterPosition >= (chapter.characterPosition || 0)
            );

          setCurrentChapter(currentChapterItem || null);
        }

        // Уведомляем родительский компонент о изменении позиции через стабильные ссылки
        if (callbacksRef.current.onPositionChange && !isInitializing) {
          callbacksRef.current.onPositionChange(
            characterPosition,
            location,
            fraction,
            cfi
          );
        }

        // Уведомляем о прогрессе чтения через стабильные ссылки (но не во время инициализации)
        if (callbacksRef.current.onProgressChange && !isInitializing) {
          callbacksRef.current.onProgressChange(
            progressPercent,
            characterPosition
          );
        }
      });

      // Настройка внешнего вида (если поддерживается)
      if (view.style) {
        view.style.width = "100%";
        view.style.height = "100%";
        // fontSize будет установлен отдельным useEffect
      }

      // Режим чтения будет установлен отдельным useEffect

      const response = await fetch(bookUrl, { signal: abortController.signal });
      if (!response.ok) {
        throw new Error(`Failed to load book: ${response.statusText}`);
      }

      if (!isMountedRef.current) return;

      const arrayBuffer = await response.arrayBuffer();
      const file = new File([arrayBuffer], "book.fb2", {
        type: "application/x-fictionbook+xml",
      });

      if (!isMountedRef.current) return;

      const book = await makeFB2(file);
      await view.open(book);

      if (!isMountedRef.current) return;

      // Настраиваем renderer сразу после открытия книги
      configureRenderer();

      // Note: We no longer use foliate's TOC for navigation, we use custom TOC with characterPosition
      // setBookToc(book.toc || []);

      console.log("book.toc", { book, initialCfi });

      // Стили будут применены отдельным useEffect

      // Получаем общее количество символов из метаданных после загрузки
      const totalChars = characterLength || 100000;
      setTotalCharacters(totalChars);

      // Проверяем URL параметры для навигации
      let navigated = false;

      // Приоритет 1: foliateHref parameter (точность 100%)
      const hrefParam = searchParams.get("href");
      if (hrefParam && !navigated) {
        try {
          // Преобразуем строки-числа в числа для foliate
          const target = isNaN(hrefParam) ? hrefParam : parseInt(hrefParam, 10);
          console.log("🎯 Going to foliate href:", hrefParam, "→", target);
          await view.goTo(target);
          navigated = true;

          // Clean up URL parameter
          const newParams = new URLSearchParams(searchParams);
          newParams.delete("href");
          setSearchParams(newParams, { replace: true });
        } catch (error) {
          console.error("Navigation by foliate href failed:", error);
        }
      }

      // Приоритет 2: Position parameter (точность ~95%)
      const posParam = searchParams.get("pos");
      if (posParam && !navigated) {
        const characterPosition = parseInt(posParam, 10);
        if (!isNaN(characterPosition) && totalChars > 0) {
          // Use Decimal.js for precise fraction calculation
          const fraction = new Decimal(characterPosition)
            .dividedBy(totalChars)
            .toNumber();

          console.log(
            "🎯 Going to position:",
            characterPosition,
            "fraction:",
            fraction
          );
          await view.goTo({ fraction });
          navigated = true;

          // Clean up URL parameter
          const newParams = new URLSearchParams(searchParams);
          newParams.delete("pos");
          setSearchParams(newParams, { replace: true });
        }
      }

      // Приоритет 3: CFI parameter (fallback для совместимости)
      const cfiParam = searchParams.get("cfi");
      if (cfiParam && !initialCfi && !navigated) {
        console.log("🎯 Going to shared CFI position:", cfiParam);
        await view.goTo(cfiParam);
        navigated = true;

        // Clean up URL parameter
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("cfi");
        setSearchParams(newParams, { replace: true });
      }

      if (!navigated) {
        if (initialCfi) {
          // Используем точную сохраненную позицию через CFI
          console.log("initialCfi", initialCfi);

          setTimeout(async () => {
            if (!isMountedRef.current) return;
            console.log("🎯 Going to saved CFI position:", initialCfi);
            await view.goTo(initialCfi);
            console.log("✅ goTo CFI completed");
          }, 1000);
        } else if (initialFraction !== undefined && initialFraction !== null) {
          // Fallback на fraction если CFI нет
          console.log("initialFraction fallback", initialFraction);

          if (!isMountedRef.current) return;
          console.log("🎯 Going to saved fraction position:", initialFraction);
          await view.goToFraction(initialFraction);
          console.log("✅ goToFraction completed");
        } else {
          view.renderer.next();
        }
      } else {
        // Если была навигация по hash, тоже сбрасываем флаг инициализации
      }

      setTimeout(() => {
        isInitializing = false;
      }, 1000);

      if (!isMountedRef.current) return;
      setIsLoading(false);
    } catch (err) {
      // Don't update state if component is unmounted or request was aborted
      if (!isMountedRef.current || abortController.signal.aborted) return;

      console.error("Failed to initialize foliate reader:", err);
      setError(err.message);
      setTimeout(() => {
        isInitializing = false;
      }, 1000);

      setIsLoading(false);

      if (callbacksRef.current.onError) {
        callbacksRef.current.onError(err);
      }
    }
  }, [bookUrl, bookMetadata, configureRenderer]);

  // Инициализация при монтировании или изменении URL
  useEffect(() => {
    isMountedRef.current = true;
    initializeFoliate();

    // Очистка при размонтировании
    return () => {
      isMountedRef.current = false;

      // Abort any ongoing async operations
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      if (viewRef.current) {
        // Удаляем элемент из DOM
        if (viewRef.current.parentNode) {
          viewRef.current.parentNode.removeChild(viewRef.current);
        }
        viewRef.current = null;
      }
    };
  }, [initializeFoliate]);

  // Обновление стилей при изменении настроек
  useEffect(() => {
    if (isLoading) return;

    if (viewRef.current?.renderer?.setStyles) {
      const css = getFoliateCSS(readerTheme, {
        spacing,
        justify,
        hyphenate,
        fontSize,
      });

      viewRef.current.renderer.setStyles(css);
    }

    // Обновляем размер шрифта на элементе view (для совместимости)
    if (viewRef.current && viewRef.current.style) {
      viewRef.current.style.fontSize = `${fontSize}px`;
    }
  }, [isLoading, readerTheme, fontSize, spacing, justify, hyphenate]);

  // Функция для настройки renderer
  const configureRenderer = useCallback(() => {
    if (!viewRef.current?.renderer) return;

    const renderer = viewRef.current.renderer;
    renderer.setAttribute("flow", "paginated");
    renderer.setAttribute("spread", "auto");
    renderer.setAttribute("animated", true);
    renderer.setAttribute("gap", 10);
  }, []);

  // Устанавливаем постраничный режим при инициализации
  useEffect(() => {
    configureRenderer();
  }, [viewRef.current?.renderer, configureRenderer]);

  // Обработка клавиатуры для навигации
  useKeyboardNavigation({
    onGoLeft: () => viewRef.current?.goLeft?.(),
    onGoRight: () => viewRef.current?.goRight?.(),
  });

  // Определение текущей главы на основе позиции в книге
  useEffect(() => {
    if (!bookMetadata?.tableOfContents || !currentCharacter) {
      setCurrentChapter(null);
      return;
    }

    const tableOfContents = bookMetadata.tableOfContents;
    let foundChapter = null;

    // Ищем главу, в которой находится текущая позиция
    for (let i = tableOfContents.length - 1; i >= 0; i--) {
      const chapter = tableOfContents[i];
      if (
        chapter.characterPosition &&
        currentCharacter >= chapter.characterPosition
      ) {
        foundChapter = chapter;
        break;
      }
    }

    // Если не нашли по точной позиции, используем приблизительный расчет
    if (!foundChapter && totalCharacters > 0) {
      const progress = currentCharacter / totalCharacters;
      const chapterIndex = Math.floor(progress * tableOfContents.length);
      foundChapter =
        tableOfContents[Math.min(chapterIndex, tableOfContents.length - 1)];
    }

    setCurrentChapter(foundChapter);
  }, [currentCharacter, totalCharacters, bookMetadata?.tableOfContents]);

  // Обработчики с сохранением в localStorage
  const handleFontSizeChange = (newFontSize) => {
    updateFontSize(newFontSize);
  };

  const handleReaderThemeChange = (newTheme) => {
    updateTheme(newTheme);
  };

  const handleDarkModeChange = (isDark) => {
    const newTheme = isDark ? "dark" : "light";
    updateTheme(newTheme);
  };

  const handleSpacingChange = (newSpacing) => {
    updateSpacing(newSpacing);
  };

  const handleJustifyChange = (newJustify) => {
    updateJustify(newJustify);
  };

  const handleHyphenateChange = (newHyphenate) => {
    updateHyphenate(newHyphenate);
  };

  // Режим чтения зафиксирован как "paginated"

  // Методы управления для передачи в children
  const readerControls = {
    // Навигация
    goToNextPage: () => viewRef.current?.goRight?.(),
    goToPrevPage: () => viewRef.current?.goLeft?.(),
    goToPosition: (characterPosition) => {
      if (viewRef.current && totalCharacters > 0) {
        try {
          // Clamp position to valid range
          const clampedPosition = Math.max(
            0,
            Math.min(characterPosition, totalCharacters)
          );

          // Use Decimal.js for precise calculation
          const fraction = new Decimal(clampedPosition)
            .dividedBy(totalCharacters)
            .toNumber();

          viewRef.current.goTo?.({ fraction });
        } catch (error) {
          console.error(
            "Position navigation failed, staying at current position:",
            error
          );
        }
      }
    },
    goToChapter: (chapterIndex) => {
      // Получаем информацию о главе из нашего GraphQL tableOfContents
      const graphqlTableOfContents = bookMetadata?.tableOfContents || [];
      const targetChapter = graphqlTableOfContents[chapterIndex];

      if (!viewRef.current || !targetChapter) {
        console.warn("Cannot navigate: missing data");
        return;
      }

      // ПРИОРИТЕТ 1: foliateHref (точность 100%)
      if (targetChapter.foliateHref) {
        try {
          // Преобразуем строки-числа в числа для foliate
          const target = isNaN(targetChapter.foliateHref)
            ? targetChapter.foliateHref
            : parseInt(targetChapter.foliateHref, 10);

          console.log(
            "📖 Navigating by foliate href:",
            targetChapter.title,
            "href:",
            targetChapter.foliateHref,
            "→",
            target
          );
          viewRef.current.goTo?.(target);
          return; // Успех!
        } catch (error) {
          console.warn("Foliate href navigation failed:", error);
        }
      }

      // ПРИОРИТЕТ 2: characterPosition (точность ~95%)
      const { characterPosition } = targetChapter;
      if (
        characterPosition !== undefined &&
        characterPosition !== null &&
        totalCharacters
      ) {
        try {
          const fraction = new Decimal(characterPosition)
            .dividedBy(totalCharacters)
            .toNumber();

          console.log(
            "📖 Navigating by characterPosition (fallback):",
            targetChapter.title,
            "pos:",
            characterPosition
          );
          viewRef.current.goTo?.({ fraction });
        } catch (error) {
          console.error("Position navigation failed:", error);
        }
      }
    },

    // Настройки темы и отображения
    fontSize,
    setFontSize: handleFontSizeChange,
    darkMode: readerTheme === "dark",
    setDarkMode: handleDarkModeChange,
    readerTheme,
    setReaderTheme: handleReaderThemeChange,
    readingMode,
    setReadingMode: () => {}, // Readonly - только paginated mode
    spacing,
    setSpacing: handleSpacingChange,
    justify,
    setJustify: handleJustifyChange,
    hyphenate,
    setHyphenate: handleHyphenateChange,

    // Состояние
    readingProgress,
    currentCharacter,
    totalCharacters,
    currentCfi,
    isLoading,
    error,

    // Оглавление и навигация по главам
    tableOfContents: bookMetadata?.tableOfContents || [],
    currentChapter,
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        position: "relative",
        backgroundColor: readerTheme === "dark" ? "#121212" : "#ffffff",
      }}
    >
      {/* Лоадер поверх контейнера */}
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backgroundColor: "background.default",
            zIndex: 1000,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Загрузка книги...
          </Typography>
        </Box>
      )}
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          position: "relative",
        }}
      ></Box>
      {/* Прозрачный overlay для перехвата событий */}
      <Box
        ref={overlayRef}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 10,
          backgroundColor: "transparent",
          cursor: "pointer",
        }}
      />
      {/* Визуальные зоны навигации */}
      <>
        {/* Левая зона - предыдущая страница */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "25%",
            height: "100%",
            zIndex: 5,
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0,
            transition: "opacity 0.2s ease",
            "&:hover": {
              opacity: 1,
            },
          }}
        ></Box>

        {/* Правая зона - следующая страница */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "25%",
            height: "100%",
            zIndex: 5,
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0,
            transition: "opacity 0.2s ease",
            "&:hover": {
              opacity: 1,
            },
          }}
        ></Box>
      </>

      {/* Ошибка поверх контейнера */}
      {error && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            p: 3,
            backgroundColor: "background.default",
            zIndex: 1000,
          }}
        >
          <Typography variant="h6" color="error">
            Ошибка загрузки книги
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {error}
          </Typography>
        </Box>
      )}
      {/* Render props - передаем управление в children */}
      {children && typeof children === "function" && children(readerControls)}
    </Box>
  );
}

FoliateReader.propTypes = {
  bookUrl: PropTypes.string.isRequired,
  bookMetadata: PropTypes.object,
  characterLength: PropTypes.number,
  initialFraction: PropTypes.number,
  initialCfi: PropTypes.string,
  onPositionChange: PropTypes.func,
  onProgressChange: PropTypes.func,
  onError: PropTypes.func,
  onUIToggle: PropTypes.func,
  children: PropTypes.func,
};
